// src/App.jsx
import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { IconContext } from 'react-icons/lib';
import { ErrorBoundary } from 'react-error-boundary';
import SimpleSpinner from './SimpleSpinner';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useUserProfile } from './hooks/useUserProfile';
import NotFoundPage from './pages/NotFoundPage';
import TestAuthPage from './TestAuthPage.jsx';
import LinkShopPage from './pages/shops/LinkShopPage.jsx';
import AddShopMemberPage from './pages/shops/AddShopMemberPage.jsx';
import LoginToShop from './pages/shops/LoginToShop.jsx';
import EditProductsPage from './products/EditProductsPage.jsx';
import { ImageKitProvider } from '@imagekit/react';
import Modal from './components/modals/Modal';
import { Toaster } from 'react-hot-toast';
import { NavigationHistoryProvider } from './hooks/useNavigationHistrory';
import Spinner from './components/Spinner';
import ProfileSyncWatcher from './components/ProfileSyncWatcher';
import TestGlobalTimer from './TestGlobalTimer';
// import './pages/HomePage/HomeTabs/clip.css';

import { useDataUsageTracker } from './hooks/useDataUsageTracker';
// 1. IMPORT YOUR REAL ANALYTICS HOOK
import { useAnalyticsTracker } from './hooks/useAnalyticsTracker';

// Import runInitialLanguageCheck
import { runInitialLanguageCheck } from './components/modals/AppBootStrap';
runInitialLanguageCheck();

const HomePage = lazy(() => import('./pages/HomePage'));

// ------------------------ Shop -----------------------------
const NewShopDashboard = lazy(() => import('./pages/shop_dashboard/NewShopDashboard'));
const ShopSetupPage = lazy(() => import('./pages/shops/ShopSetupPage.jsx'));
const DeleteShop = lazy(() => import('./pages/users/DeleteShopPage.jsx'));
const BusinessPageRenderer = lazy(() => import('./pages/BusinessPages/BusinessPageRenderer'));
const BusinessPageEditor = lazy(() => import('./pages/BusinessPages/BusinessPageEditor'));
const ShopGuard = lazy(() => import('./pages/variants/ShopGuard.jsx'));

const CreateProducts = lazy(() => import('./products/CreateProducts.jsx'));
const UpdateProducts = lazy(() => import('./products/updateProducts/UpdateProducts.jsx'));
const CreateProductSlides = lazy(() => import('./products/CreateCarouselComponents/ProductCarouselBuilder.jsx'));
const ShopCreationPage = lazy(() => import('./pages/shops/ShopCreationPage.jsx'));
const AdminEditShopPage = lazy(() => import('./pages/shops/AdminEditShopPage.jsx'));

const VariantDashboard = lazy(() => import('./pages/variants/VariantDashboard.jsx'));
const ShopProductCreate = lazy(() => import('./pages/shop_dashboard/ShopProductCreate.jsx'));

const ShopOnboardingForm = lazy(() => import('./pages/shops/ShopOnboardingForm.jsx'));
const ShopAppearanceSettings = lazy(() => import('./pages/shop_dashboard/ShopAppearanceSettings.jsx'));


// ------------------------ Product -----------------------------
const ProductPage = lazy(() => import('./pages/ProductPage'));
const SearchResults = lazy(() => import('./pages/SearchResultsPage'));
const ElectronicsPage = lazy(() => import('./pages/Categories/ElectronicsHome.jsx.jsx'));

const AdminShopProductApprovalPage = lazy(() => import('./pages/new_approval/AdminShopProductApprovalPage.jsx'));
const ShopProductSubmissionPage = lazy(() => import('./pages/new_approval/ShopProductSubmissionPage.jsx'));
const ProductApproval = lazy(() => import('./pages/shop_dashboard/ProductApproval.jsx'));
const ProductElectronicsCardUpdate = lazy(() => import('./pages/shop_dashboard/ProductElectronicsCardUpdate.jsx'));


// ------------------------ User -----------------------------
const SignInPage = lazy(() => import('./pages/users/SignInPage.jsx'));
const SignUpPage = lazy(() => import('./pages/users/SignUpPage.jsx'));
const LogoutPage = lazy(() => import('./pages/users/LogoutPage.jsx'));
const DeleteAccount = lazy(() => import('./pages/users/DeleteAccountPage.jsx'));

const ForgotPasswordPage = lazy(() => import('./pages/users/ForgotPasswordPage.jsx'));
const ResetPasswordPage = lazy(() => import('./pages/users/ResetPasswordPage.jsx'));
const AddPasswordPage = lazy(() => import('./pages/users/AddPasswordPage.jsx'));

const UserProfile = lazy(() => import('./pages/users/UserProfile.jsx'));
const AccountsPage = lazy(() => import('./pages/AccountsPage.jsx'));
const PublicProfile = lazy(() => import('./pages/users/PublicProfile.jsx'));
const ProProfile = lazy(() => import('./pages/users/ProProfile.jsx'));
const UserSettings = lazy(() => import('./pages/UserSettings'));

const MyOrderPage = lazy(() => import('./pages/reservations/MyOrderPage.jsx'));
const MyFavorites = lazy(() => import('./pages/sections/FavoritesPage'));
const ShareCoins = lazy(() => import('./pages/BusinessPages/ShareCoins'));
const ShareMasterKey = lazy(() => import('./pages/BusinessPages/ShareMasterKey'));
const ShareUserKey = lazy(() => import('./pages/BusinessPages/ShareUserKey'));


// ------------------------ Admin -----------------------------
const AdminCreationPage = lazy(() => import('./pages/Admin/AdminCreationPage.jsx'));
const AdminSignInPage = lazy(() => import('./pages/Admin/AdminSignInPage.jsx'));
const AdminDashboardPage = lazy(() => import('./pages/Admin/AdminDashboardPage.jsx'));
const CreateMasterKey = lazy(() => import('./pages/Admin/CreateMasterKey.jsx'));
const UserManagementPage = lazy(() => import('./pages/Admin/UserManagementPage.jsx'));
const AccessDeniedPage = lazy(() => import('./pages/AccessDeniedPage.jsx'));
const AdminUpdateShopReputationQuarterly = lazy(() => import('./pages/AdminUpdateShopReputationQuarterly'));
const AdminComplaintsPage = lazy(() => import('./pages/Admin/AdminComplaintsPage.jsx'));

const PurgeImages = lazy(() => import('./pages/PurgeImages'));
const MasterKeyMarket = lazy(() => import('./pages/users/MasterkeyMarket.jsx'));


// ------------------------ Test -----------------------------
const TestPage = lazy(() => import('./pages/sections/Test'));
const TestMenu4Steps = lazy(() => import('./pages/sections/Test2'));
const TestCardsCarousel = lazy(() => import('./pages/shop_dashboard/Test4'));

const TestMenuCategoryStrips = lazy(() => import('./pages/shop_dashboard/TestMenu.jsx'));
const TestMenuItemsLayout = lazy(() => import('./pages/shop_dashboard/TestMenu2.jsx'));

const TestNewSearch = lazy(() => import('./pages/TestSearch/TestNewSearch.jsx'));
const TestNewCarousel = lazy(() => import('./pages/TestSearch/NewCarousel/TestNewCarousel.jsx'));
const TestGames = lazy(() => import('./pages/TestGames/Test'));
const TestMap = lazy(() => import('./pages/TestMap/TestMap'));
const TestShopPages = lazy(() => import('./pages/shop_Pages/First'));
const TestShopFeature_1 = lazy(() => import('./pages/shops/components/FeaturedOfferingsEditor'));
const TestShopsearchTags = lazy(() => import('./pages/shops/components/SearchTagsEditor'));

const TestProsServicePage = lazy(() => import('./pages/BusinessPages/ProServicePages/Default/ProServicePageTemplate'));
const TestCommercialHbPage = lazy(() => import('./pages/BusinessPages/CommercialHubPages/Default/Restaurant_1'));
const ImageUploader = lazy(() => import('./pages/ImageUploader.jsx'));


const AdminGatePage = lazy(() => import('./pages/Admin/AdminGatePage.jsx'));
const AdminShopApproval = lazy(() => import('./pages/Admin/AdminShopApproval.jsx'));
const AdminCrudTemplateService = lazy(() => import('./pages/shop_dashboard/AdminTemplateManager.jsx'));
const AnalyticsDashboardPage = lazy(() => import('./pages/Admin/AnalyticsDashboardPage'));

// ------------------------ Ads -----------------------------
const AdvertiserDashboard = lazy(() => import('./pages/ads/AdvertiserDashboard.jsx'));
const CampaignCreatePage = lazy(() => import('./pages/ads/CampaignCreatePage.jsx'));
const CampaignPerformancePage = lazy(() => import('./pages/ads/CampaignPerformancePage.jsx'));
const ExternalAdvertiserApplyPage = lazy(() => import('./pages/ads/ExternalAdvertiserApplyPage.jsx'));
const AdModerationPage = lazy(() => import('./pages/Admin/AdModerationPage.jsx'));

// ------------------------ App -----------------------------
const TransitionOverlay = lazy(() => import('./components/TransitionsOverlay'));
const TestTransitions = lazy(() => import('./components/TestTransitions'))
const ContactSupport = lazy(() => import('./pages/support/ContactSupport.jsx'));
const DataUsagePage = lazy(() => import('./pages/DataUsagePage.jsx'));


// const UserFavorites = lazy(() => import('./pages/UserFavorites'));
// const OwnerUseTemplateService = lazy(() => import('./pages/shop_dashboard/TemplateManager.jsx'));
// const OldShopDashboard = lazy(() => import('./pages/shop_dashboard/OldShopDashboard.jsx'));
// const TestArtisanPage = lazy(() => import('./pages/BusinessPages/ArtisanPages/Default/ArtisanPageTemplate'));
// don't touch these above


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    },
  },
});

const RoleBasedRoute = ({ children, requiredRoles }) => {
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading, isError } = useUserProfile();
  const [hasCheckedProfile, setHasCheckedProfile] = useState(false);

  useEffect(() => {
    if (!authLoading && !profileLoading) {
      setHasCheckedProfile(true);
    }
  }, [authLoading, profileLoading]);

  if (authLoading || profileLoading || !hasCheckedProfile) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  if (isError || !profile) {
    return <Navigate to="/access-denied" replace />;
  }

  const currentUserRole = profile.role;
  const hasRequiredRole = requiredRoles.includes(currentUserRole);

  if (!hasRequiredRole) {
    return <Navigate to="/access-denied" replace />;
  }

  return children;
};

const AppRoutes = () => {
  // 2. INITIALIZE TRACKER HERE - RUNS SYSTEM-WIDE UNDER ROUTER CONTEXT
  useAnalyticsTracker();
  useDataUsageTracker();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/categories/electronics" element={<ElectronicsPage />} />
      <Route path="/products/:productId" element={<ProductPage />} />

      <Route path="/accounts" element={<AccountsPage />} />
      <Route path="/userprofile/public/:public_id" element={<PublicProfile />} />
      <Route path="/userprofile/pro/:public_id" element={<ProProfile />} />
      <Route path="/my-favorites" element={<MyFavorites />} />

      <Route path="/shareCoins" element={<ShareCoins />} />
      <Route path="/share-masterkey" element={<ShareMasterKey />} />
      <Route path="/share-userkey" element={<ShareUserKey />} />
      <Route path="/masterkey-market" element={<MasterKeyMarket />} />


      <Route path="/shops/:businessSlug" element={<BusinessPageRenderer />} />
      <Route path="/business-page-editor" element={<BusinessPageEditor />} />

      <Route path="shop/appearance" element={<ShopAppearanceSettings />} />

      {/* Auth routes */}
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/sign-up" element={<SignUpPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      {/* NEW: password recovery + add-password */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/add-password" element={
        <RoleBasedRoute requiredRoles={['user_1', 'user_2', 'user_3', 'shop_owner', 'shop_manager', 'shop_staff', 'admin_1', 'admin_2', 'admin_3']}>
          <AddPasswordPage />
        </RoleBasedRoute>
      } />

      <Route path="/admin/sign-in" element={<AdminSignInPage />} />
      <Route path="/admin/updateshopreputationquarterly" element={<AdminUpdateShopReputationQuarterly />} />


       {/* Old Approval */}
      <Route path="/shops/products/oldapprove" element={<ShopProductCreate />} />
      <Route path="/admin/products/approve" element={<ProductApproval />} />
       {/* New Approval */}
      <Route path="/admin/products/admin-newapprove" element={<AdminShopProductApprovalPage />} />
      <Route path="/shops/products/newapprove" element={<ShopProductSubmissionPage />} />
      <Route path="/admin/products/electronics/card-update" element={<ProductElectronicsCardUpdate />} />


      <Route path="/products/update/accessories" element={<EditProductsPage />} />
      <Route path="/update-products" element={<UpdateProducts />} />



      {/* Role-based protected routes */}
      <Route path="/userprofile" element={
        <RoleBasedRoute requiredRoles={['user_1', 'user_2', 'user_3', 'shop_owner', 'admin_1', 'admin_2', 'admin_3']}>
          <UserProfile />
        </RoleBasedRoute>
      } />
      <Route path="/usersettings" element={
        <RoleBasedRoute requiredRoles={['user_1', 'user_2', 'user_3', 'shop_owner', 'admin_1', 'admin_2', 'admin_3']}>
          <UserSettings />
        </RoleBasedRoute>
      } />

      <Route path="/my-orders" element={
        <RoleBasedRoute requiredRoles={['user_1', 'user_2', 'user_3', 'shop_owner', 'admin_1', 'admin_2', 'admin_3']}>
          <MyOrderPage />
        </RoleBasedRoute>
      } />

      {/* Shop pages */}
      <Route path="/shop-creation" element={
        <RoleBasedRoute requiredRoles={['admin_1', 'admin_2', 'admin_3']}>
          <ShopCreationPage />
        </RoleBasedRoute>
      } />
      <Route path="/admin-edit-shop" element={
        <RoleBasedRoute requiredRoles={['admin_1', 'admin_2', 'admin_3']}>
          <AdminEditShopPage />
        </RoleBasedRoute>
      } />
      <Route path="/create-products" element={
        <RoleBasedRoute requiredRoles={['user_1', 'shop_owner', 'shop_manager', 'shop_staff', 'admin_1', 'admin_2', 'admin_3']}>
          <CreateProducts />
        </RoleBasedRoute>
      } />
      <Route path="/create-product-slides" element={
        <RoleBasedRoute requiredRoles={['user_1', 'shop_owner', 'shop_manager', 'shop_staff', 'admin_1', 'admin_2', 'admin_3']}>
          <CreateProductSlides />
        </RoleBasedRoute>
      } />
      <Route path="/shop/login" element={
        <RoleBasedRoute requiredRoles={['user_1', 'shop_owner', 'shop_manager', 'shop_staff', 'admin_1', 'admin_2', 'admin_3']}>
          <LoginToShop />
        </RoleBasedRoute>
      } />
      <Route path="/shop-member-add" element={
        <RoleBasedRoute requiredRoles={['user_1', 'shop_owner', 'shop_manager', 'shop_staff', 'admin_1', 'admin_2', 'admin_3']}>
          <AddShopMemberPage />
        </RoleBasedRoute>
      } />
      <Route path="/shop-link-add" element={
        <RoleBasedRoute requiredRoles={['user_1', 'shop_owner', 'shop_manager', 'shop_staff', 'admin_1', 'admin_2', 'admin_3']}>
          <LinkShopPage />
        </RoleBasedRoute>
      } />
      <Route path="/shop/dashboard" element={
        <RoleBasedRoute requiredRoles={['user_1', 'shop_owner', 'shop_manager', 'shop_staff', 'admin_1', 'admin_2', 'admin_3']}>
          <ShopGuard>
            <NewShopDashboard />
          </ShopGuard>
        </RoleBasedRoute>
      } />

      {/* Admin 1 pages */}
     <Route path="/admin/purge-images" element={
        <RoleBasedRoute requiredRoles={['admin_1', 'admin_2', 'admin_3']}>
      <PurgeImages />
        </RoleBasedRoute>
      } />

      {/* Admin 2 pages */}

      {/* Admin 3 pages */}
      <Route path="/admin/dashboard" element={
        <RoleBasedRoute requiredRoles={['admin_3']}>
          <AdminDashboardPage />
        </RoleBasedRoute>
      } />
      <Route path="/admin/createmasterkey" element={
        <RoleBasedRoute requiredRoles={['user_1', 'shop_owner', 'shop_manager', 'shop_staff', 'admin_1', 'admin_2', 'admin_3']}>
          <CreateMasterKey />
        </RoleBasedRoute>
      } />
      <Route path="/admin/manage-users" element={
        <RoleBasedRoute requiredRoles={['admin_3']}>
          <UserManagementPage />
        </RoleBasedRoute>
      } />
      <Route path="/admin/create-admin" element={
        <RoleBasedRoute requiredRoles={['admin_3']}>
          <AdminCreationPage />
        </RoleBasedRoute>
      } />

      {/* 3. NEW CLEAN PRODUCTION ROUTE FOR SUPER ADMIN ANALYTICS */}
      <Route path="/admin/analytics" element={
        <RoleBasedRoute requiredRoles={['admin_3']}>
          <AnalyticsDashboardPage />
        </RoleBasedRoute>
      } />


      <Route path="/admin" element={<AdminGatePage />} />
      <Route path="/admin/complaints" element={<AdminComplaintsPage />} />

         <Route path="/admin/ads/moderation" element={
           <RoleBasedRoute requiredRoles={['admin_3']}>
            <AdModerationPage />
             </RoleBasedRoute>
        } />
      <Route path="/admin/crud-menu-template" element={<AdminCrudTemplateService />} />
      <Route path="/admin/shop-approval" element={<AdminShopApproval />} />
      

         <Route path="/ads" element={<AdvertiserDashboard />} />
         <Route path="/ads/create" element={<CampaignCreatePage />} />
         <Route path="/ads/performance/:campaignId" element={<CampaignPerformancePage />} />
         <Route path="/ads/apply" element={<ExternalAdvertiserApplyPage />} />
         <Route path="/ads/login" element={<AdvertiserLoginPage />} />


      <Route path="/shops/onboarding" element={<ShopOnboardingForm />} />
      <Route path="/shops/setup/:businessSlug" element={<ShopSetupPage />} />
      <Route path="/delete-shop" element={<DeleteShop />} />


      <Route path="*" element={<NotFoundPage />} />
      <Route path="/contact-support" element={<ContactSupport />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />



      <Route path="/test" element={<TestPage />} />
      <Route path="/test2" element={<TestMenu4Steps />} />
      <Route path="/test4" element={<TestCardsCarousel />} />
      <Route path="/testmenu" element={<TestMenuCategoryStrips />} />
      <Route path="/testMenu2" element={<TestMenuItemsLayout />} />
      <Route path="/imageUploader" element={<ImageUploader />} />
      <Route path="/data-usage" element={<DataUsagePage />} />

      <Route path="/test-auth" element={<TestAuthPage />} />
      <Route path="/test-new-search" element={<TestNewSearch />} />
      <Route path="/test-new-carousel" element={<TestNewCarousel />} />

      <Route path="/test-map" element={<TestMap />} />
      <Route path="/test-transitions" element={<TestTransitions />} />
      <Route path="/test-games" element={<TestGames />} />

      <Route path="/test-shop-pages" element={<TestShopPages />} />
      <Route path="/test-shop-feature_1" element={<TestShopFeature_1 />} />
      <Route path="/test-shop-feature_2" element={<TestShopsearchTags />} />
      <Route path="/test-proservice" element={<TestProsServicePage />} />
      <Route path="/test-commercial" element={<TestCommercialHbPage />} />
      <Route path="/variant-dashboard" element={<VariantDashboard />} />
    
     {/* <Route path="/test-artisan" element={<TestArtisanPage />} /> */}
     {/* <Route path="/owner-use-template-service" element={<OwnerUseTemplateService />} /> */}

    </Routes>
  );
};

const imagekitPublicKey = import.meta.env.VITE_IMAGEKIT_PUBLIC_KEY;
const imagekitUrlEndpoint = import.meta.env.VITE_IMAGEKIT_URL_ENDPOINT;

// Cleaned global provider configuration
import AuthLocationWatcher from './components/modals/AuthLocationWatcher';
import UpdatePrompt from './pages/shop_dashboard/UpdatePrompt';
import PushPermissionModal from './components/PushPermissionModal';
import InstallAppBanner from './components/InstallAppBanner';
import NotificationSyncWatcher from './components/NotificationSyncWatcher';
import DataUsageBanner from './pages/DataUsageBanner';
import AdvertiserLoginPage from './pages/ads/AdvertiserLoginPage';

// A render crash anywhere in the tree used to just go blank/unresponsive with
// no visible error - react-error-boundary was already a dependency but never
// wired up. This catches it and gives the user a way out instead of a dead
// screen (this is what several "nothing happens when I click X" reports
// were most likely masking).
function ErrorFallback({ error, resetErrorBoundary }) {
  console.error('[App] Uncaught render error:', error);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-gray-50">
      <h2 className="text-lg font-bold text-slate-900 mb-2">Something went wrong</h2>
      <p className="text-sm text-slate-500 mb-5">Please reload to keep using Porte17.</p>
      <button
        onClick={() => {
          resetErrorBoundary();
          window.location.reload();
        }}
        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-xl text-sm uppercase tracking-wider transition-all"
      >
        Reload
      </button>
    </div>
  );
}

const App = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Router>
        <AuthProvider>
          <LanguageProvider>
            <IconContext.Provider value={{ color: '#ffffff', size: '2rem' }}>
              <QueryClientProvider client={queryClient}>
                <ImageKitProvider publicKey={imagekitPublicKey} urlEndpoint={imagekitUrlEndpoint}>
                  <AuthLocationWatcher/>
                  <ProfileSyncWatcher />
                  <NotificationSyncWatcher />
                  <NavigationHistoryProvider>
                    <Suspense fallback={<SimpleSpinner />}>
                      {/* <DataUsageBanner /> */}
                      <AppRoutes />
                      <TransitionOverlay />
                      <Modal />
                      <Toaster position='top-center' />
                      <UpdatePrompt />
                      <PushPermissionModal />
                      <InstallAppBanner />
                    </Suspense>
                  </NavigationHistoryProvider>
                </ImageKitProvider>
              </QueryClientProvider>
            </IconContext.Provider>
          </LanguageProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;